import { Avatar, TextStyle } from '@superops/ui-design-system';
import React from 'react';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTranslatedText, useUserMSPCheckHook } from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useChatFeatureEnabled } from '@nile/components/src/Pages/SystemTrayChatPage/utils';

const ConcurrentViewerContainer = styled.div`
  .Superops-Avatar {
    width: 24px;
    min-width: 24px;
    ${(props) => props?.background && `background: ${props?.background};`}
    opacity: ${(props) => (props?.inActive ? '0.4' : '1.0')};
  }
  & .editing {
    position: absolute;
    bottom: -5px;
    right: 2px;

    .pencil {
      position: absolute;
      top: -7px;
    }
  }
`;

const ConcurrentViewers = ({ concurrentViewers: allViewers, className }) => {
  const intl = useIntl();
  const isMSP = useUserMSPCheckHook();
  const isChatAvailable = useChatFeatureEnabled();
  const flags = useFlags();

  if (!isMSP) return null;

  const concurrentViewers =
    flags?.agentChatFe && isChatAvailable
      ? allViewers?.filter((el) => el?.state !== 'LIVE_CHAT')
      : allViewers;

  const chatViewers = allViewers?.filter((el) => el?.state === 'LIVE_CHAT');

  return (
    <span className={className || 'fx center mlauto mt-10 g3'}>
      {flags?.agentChatFe &&
        isChatAvailable &&
        chatViewers?.map((viewer) => {
          const { user, isTyping } = viewer;
          return (
            <Tooltip
              html={getTranslatedText(
                intl,
                isTyping
                  ? 'common.collaborator.isTyping'
                  : 'common.collaborator.isViewing',
                { collabUserName: user?.name }
              )}
              delay={200}
              duration={250}
              inertia
              position="bottom-start"
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <ConcurrentViewerContainer className="position-relative flex-center g3">
                <SVGIcon name="LiveDot" size="sm" color="#3ABF38" />
                <TextStyle variation="positive">
                  <FormattedMessage id="chat.label.live" />
                </TextStyle>
                <Avatar
                  size="extraSmall"
                  name={user?.name}
                  initials={user?.name?.[0]}
                />
                {isTyping ? (
                  <span className="editing grid">
                    <span className="pencil animated-pencil fx actr">
                      <SVGIcon name="Pencil" size="xs" />
                    </span>
                    <SVGIcon name="Line" size="xs" />
                  </span>
                ) : null}
              </ConcurrentViewerContainer>
            </Tooltip>
          );
        })}
      {concurrentViewers?.slice(0, 4)?.map((viewer) => {
        const { user, state, isTyping } = viewer;
        const isActive = state === 'ACTIVE';
        return (
          <Tooltip
            html={
              <TextStyle>
                {getTranslatedText(
                  intl,
                  isActive
                    ? isTyping
                      ? 'common.collaborator.isTyping'
                      : 'common.collaborator.isViewing'
                    : 'common.collaborator.isInactive',
                  { collabUserName: user?.name }
                )}
              </TextStyle>
            }
            delay={200}
            duration={250}
            inertia
            position="bottom-start"
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
          >
            <ConcurrentViewerContainer
              className="position-relative"
              inActive={!isActive}
            >
              <Avatar
                size="extraSmall"
                name={user?.name}
                initials={user?.name?.[0]}
              />
              {isActive && isTyping ? (
                <span className="editing grid">
                  <span className="pencil animated-pencil fx actr">
                    <SVGIcon name="Pencil" size="xs" />
                  </span>
                  <SVGIcon name="Line" size="xs" />
                </span>
              ) : null}
            </ConcurrentViewerContainer>
          </Tooltip>
        );
      })}
      {concurrentViewers.length > 4 ? (
        <Tooltip
          html={
            <TextStyle>
              {concurrentViewers?.slice(4)?.map((viewer) => {
                const { user, state, isTyping } = viewer;
                const isActive = state === 'ACTIVE';
                return (
                  <div>
                    {getTranslatedText(
                      intl,
                      isActive
                        ? isTyping
                          ? 'common.collaborator.isTyping'
                          : 'common.collaborator.isViewing'
                        : 'common.collaborator.isInactive',
                      { collabUserName: user?.name }
                    )}
                  </div>
                );
              })}
            </TextStyle>
          }
          delay={200}
          duration={250}
          inertia
          position="bottom-start"
          trigger="mouseenter"
          animation="shift"
          animateFill={false}
        >
          <ConcurrentViewerContainer
            background="#A19F9F"
            className="position-relative"
          >
            <Avatar
              size="small"
              initials={`+${concurrentViewers.length - 4}`}
            />
            {concurrentViewers?.slice(4)?.some((viewer) => viewer?.isTyping) ? (
              <span className="editing grid">
                <span className="pencil animated-pencil fx actr">
                  <SVGIcon name="Pencil" size="xs" />
                </span>
                <SVGIcon name="Line" size="xs" />
              </span>
            ) : null}
          </ConcurrentViewerContainer>
        </Tooltip>
      ) : null}
    </span>
  );
};

export default ConcurrentViewers;
